import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { format, startOfMonth, getDay, getDate, getDaysInMonth, addMonths, subMonths } from 'date-fns'
import { useAuth } from '@clerk/clerk-react'
import { useGuestSession } from '@/hooks/useGuestSession'
import { ArchiveCalendar, ArchiveStats, PremiumBanner, PremiumUpsellModal } from '@/components/archive'
import { GameChallengePopup } from '@/components/GameChallengePopup'
import { useGamePopupData } from '@/hooks/useGamePopupData'
import type { PlayerProgress } from '@/types/game'
import { useArchiveCalendar } from '@/hooks/useArchiveCalendar'
import { useChallengeClick } from '@/hooks/useChallengeClick'
import { usePlayerStats } from '@/hooks/usePlayerStats'
import { useGameRegistry } from '@/hooks/useGameRegistry'
import { usePublicSettings } from '@/hooks/usePublicSettings'
import type { ArchiveChallengeStatus, PlayerStatsResponse } from '@/services/api-client'

/**
 * Transformed challenge type for calendar display
 * Converts ISO date strings to Date objects and flattens nested properties
 */
export type CalendarChallenge = Omit<ArchiveChallengeStatus, 'publishDate' | 'userProgress'> & {
  publishDate: Date;
  starsEarned?: number;
}

export type EmptyDayCell = {
  type: 'empty';
  dayNumber: number;
}

export type CalendarCell = CalendarChallenge | EmptyDayCell | null

/**
 * Type guard to check if stats response is PlayerStatsResponse (not H2HStatsResponse)
 * Archive calendars only display daily/weekly challenge games, not head-to-head games
 */
function isPlayerStats(stats: PlayerStatsResponse | any): stats is PlayerStatsResponse {
  return stats && 'stats' in stats && 'currentStreak' in stats.stats
}

export default function GameArchive() {
  const { gameId } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [upsellReason, setUpsellReason] = useState<string>('')
  const isTransitioningRef = useRef(false) // Synchronous ref to prevent race conditions

  // State for challenge popup
  const [selectedChallenge, setSelectedChallenge] = useState<CalendarChallenge | null>(null)
  const [selectedProgress, setSelectedProgress] = useState<PlayerProgress | undefined>(undefined)

  // Get game data from registry
  const { getGameById } = useGameRegistry()
  const game = useMemo(() => {
    if (!gameId) return null
    // Use game ID directly from URL parameter
    return getGameById(gameId)
  }, [gameId, getGameById])

  // Format month for API call (YYYY-MM)
  const monthString = useMemo(() => {
    return format(currentMonth, 'yyyy-MM')
  }, [currentMonth])

  // IMA-88: Use Clerk's feature-based access control instead of subscription service
  const { isLoaded: isClerkLoaded, has, isSignedIn } = useAuth()
  const guestSession = useGuestSession(isClerkLoaded, isSignedIn ?? false, { gameId: gameId ?? undefined })
  const hasArchiveAccess = has?.({ feature: 'access_archives' }) ?? false
  const isLoadingSubscription = !isClerkLoaded

  // IS-INV-034 / IMA-430: Read paywall state from backend to suppress upsell banner
  // when paywall is disabled. Frontend must not make premium/free decisions independently.
  const { settings: publicSettings, loading: settingsLoading } = usePublicSettings()
  const paywallEnabled = publicSettings?.paywall ?? true // Default to true (show banner) while loading

  // Fetch calendar data from API
  // IMPORTANT: Wait for Clerk to load before fetching calendar
  // This prevents race condition where calendar fetches with wrong access tier
  // and caches wrong data before Clerk finishes loading
  const {
    data: calendarData,
    isLoading: isLoadingCalendar,
    error: calendarError,
    refetch: refetchCalendar
  } = useArchiveCalendar({
    gameId: game?.id || null,
    month: monthString,
    // IMA-88: Use feature-based access tier instead of subscription tier
    subscriptionTier: hasArchiveAccess ? 'premium' : 'free',
    enabled: !!game?.id && !isLoadingSubscription && (isSignedIn ? true : guestSession.isReady)
  })

  // Fetch stats data from API (parallel with calendar)
  const {
    stats,
    loading: isLoadingStats,
    error: _statsError  // Prefixed with _ to indicate intentionally unused
  } = usePlayerStats(game?.id ?? null, game ?? null)

  // Fetch popup data for selected challenge
  const {
    playerStats: popupPlayerStats,
    isCurrentPeriod,
    loading: popupDataLoading,
    error: popupDataError,
    refetch: refetchPopupData
  } = useGamePopupData(
    selectedChallenge ? game?.id || null : null,
    selectedChallenge?.id?.toString() || null
  )

  // Challenge click handler
  // Note: game can be null here, but we have an early return guard below (line 202)
  // that prevents rendering when game is null, so the click handler is never called with null game
  const { handleChallengeClick: handleClickLogic, isNavigating } = useChallengeClick({
    game: game || { id: '', name: '', signatureColor: '', scoreDescriptor: '', challengeType: 'daily' } as any,
    onShowUpsellModal: (reason: string) => {
      setUpsellReason(reason)
      setIsModalOpen(true)
    }
  })

  // Get game display name from actual game data, fallback to game ID transformation
  const gameDisplayName = useMemo(() => {
    if (game?.displayName) return game.displayName
    if (!gameId) return 'Game'
    // Fallback: convert game ID to readable format: game_lineup_mlb → Lineup Mlb
    return gameId
      .replace(/^game_/, '')
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }, [game?.displayName, gameId])

  // Calculate calendar grid (full month with empty day cells for days without challenges)
  const calendarDays = useMemo((): CalendarCell[] => {
    if (!calendarData?.challenges) return []

    const start = startOfMonth(currentMonth)
    const offset = getDay(start) // 0 = Sunday, 6 = Saturday
    const daysInMonth = getDaysInMonth(currentMonth)

    const offsetCells: CalendarCell[] = Array(offset).fill(null)

    // Build a Map<dayOfMonth, CalendarChallenge> for O(1) lookup per day
    // Parse publishDate as local midnight to avoid timezone shift issues
    // (publishDate is a calendar date, not a UTC timestamp - see IMA-106/IMA-145)
    const challengesByDay = new Map<number, CalendarChallenge>()
    calendarData.challenges.forEach(challenge => {
      const date = new Date(challenge.publishDate.split('T')[0] + 'T00:00:00')
      const dayOfMonth = getDate(date)
      challengesByDay.set(dayOfMonth, {
        ...challenge,
        publishDate: date,
        starsEarned: challenge.userProgress?.starsEarned
      })
    })

    // One cell per day of month: challenge if one exists, EmptyDayCell otherwise
    const dayCells: CalendarCell[] = Array.from({ length: daysInMonth }, (_, i) => {
      const dayOfMonth = i + 1
      return challengesByDay.get(dayOfMonth) ?? ({ type: 'empty', dayNumber: dayOfMonth } as EmptyDayCell)
    })

    return [...offsetCells, ...dayCells]
  }, [currentMonth, calendarData])

  // Check if we can navigate to previous month.
  // Capped at the launch month from game.configuration.launchDate (set in admin).
  // Falls back to true (unlimited navigation) if launchDate is not configured.
  const launchDate = game?.configuration?.launchDate as string | undefined
  const canGoPrevious = useMemo(() => {
    if (!launchDate) return true
    const launchYearMonth = launchDate.substring(0, 7) // "YYYY-MM"
    const prevYearMonth = format(subMonths(currentMonth, 1), 'yyyy-MM')
    return prevYearMonth >= launchYearMonth
  }, [currentMonth, launchDate])

  // Check if we can navigate to next month (not beyond today)
  const canGoNext = useMemo(() => {
    const today = new Date()
    const nextMonth = addMonths(currentMonth, 1)
    return nextMonth <= today
  }, [currentMonth])

  // Month navigation handlers with smooth transitions
  const handlePreviousMonth = useCallback(() => {
    if (!canGoPrevious || isTransitioningRef.current) return

    isTransitioningRef.current = true // Synchronous update - prevents race conditions
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentMonth(prev => subMonths(prev, 1))
      setIsTransitioning(false)
      isTransitioningRef.current = false
    }, 150)
  }, [canGoPrevious])

  const handleNextMonth = useCallback(() => {
    if (!canGoNext || isTransitioningRef.current) return

    isTransitioningRef.current = true // Synchronous update - prevents race conditions
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentMonth(prev => addMonths(prev, 1))
      setIsTransitioning(false)
      isTransitioningRef.current = false
    }, 150)
  }, [canGoNext])

  // Keyboard navigation: Left arrow = previous, Right arrow = next
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        handlePreviousMonth()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        handleNextMonth()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handlePreviousMonth, handleNextMonth]) // Re-bind when handlers change

  // Handle challenge click - show popup instead of navigating directly
  const handleChallengeClick = (challenge: CalendarChallenge) => {
    if (!game) return

    // For locked challenges, show upsell modal directly
    if (challenge.status === 'locked') {
      const apiChallenge = {
        ...challenge,
        publishDate: challenge.publishDate.toISOString(),
      }
      handleClickLogic(apiChallenge)
      return
    }

    // For playable challenges, show the popup
    // Build progress from challenge data (minimal data needed for popup display)
    const progress: PlayerProgress | undefined = challenge.status === 'completed' ? {
      id: 0, // Not needed for display
      userId: '', // Not needed for display
      gameChallengeId: challenge.id,
      activityState: 'completed',
      starsEarned: challenge.starsEarned || 0,
      score: null,
      completionText: null,
      wins: null,
      losses: null,
      createdAt: '',
      updatedAt: ''
    } : undefined

    setSelectedChallenge(challenge)
    setSelectedProgress(progress)
  }

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false)
    setUpsellReason('')
  }

  // Handle upgrade click
  const handleUpgrade = () => {
    setIsModalOpen(false)
    navigate('/subscription')
  }

  // Handle retry on error
  const handleRetry = () => {
    refetchCalendar()
  }

  // Handle popup close
  const handlePopupClose = () => {
    setSelectedChallenge(null)
    setSelectedProgress(undefined)
  }

  // Handle popup play button
  const handlePopupPlay = () => {
    if (!game || !selectedChallenge) return

    // Close popup first
    handlePopupClose()

    // Navigate to the game
    const gameUrl = `/games/${game.id}/play/${selectedChallenge.id}`
    navigate(gameUrl, {
      state: {
        game,
        challenge: {
          ...selectedChallenge,
          publishDate: selectedChallenge.publishDate.toISOString()
        },
        progress: selectedProgress
      }
    })
  }

  // Show error state if game not found
  if (!game) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Game Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">The game you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/games')}
            className="px-4 py-2 bg-gray-800 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
          >
            Back to Games
          </button>
        </div>
      </div>
    )
  }

  // Show error state with retry button
  if (calendarError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Error State */}
        <main className="max-w-6xl mx-auto px-6 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Failed to Load Archive</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {calendarError.message || 'Something went wrong loading the calendar.'}
            </p>
            <button
              onClick={handleRetry}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-800 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Try Again
            </button>
          </div>
        </main>
      </div>
    )
  }

  // IS-INV-034 / IMA-430: isPremium controls upsell banner visibility.
  // When paywall is disabled, treat all users as premium (no banner).
  // When paywall is enabled, fall back to Clerk feature check.
  const isPremium = !paywallEnabled || hasArchiveAccess

  // Transform stats data for ArchiveStats component
  // Only process if stats is PlayerStatsResponse (archive calendars don't support H2H games)
  const statsData = stats && isPlayerStats(stats) ? {
    totalPlayed: stats.stats.totalPlayed,
    successRate: stats.stats.successRate,
    avgScore: stats.stats.averageScore,
    totalStars: stats.stats.totalStars // IMA-97: Display total stars from API
  } : null

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Archive Header */}
        <div className="text-center mb-10">
          <h1 className="text-5xl font-bold uppercase tracking-[3px] mb-2"
              style={{ fontFamily: 'Bebas Neue, sans-serif', color: game.signatureColor }}>
            {gameDisplayName} Archives
          </h1>
          <p className="text-lg font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide"
             style={{ fontFamily: 'Oswald, sans-serif' }}>
            {game.tagline || 'Play past challenges'}
          </p>
        </div>

        {/* Premium Banner (shown only for free users when paywall is enabled, hidden while loading to prevent flash) */}
        {!isPremium && !isLoadingSubscription && !settingsLoading && (
          <PremiumBanner gameDisplayName={gameDisplayName} />
        )}

        {/* Month Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 mb-6 shadow-sm flex items-center justify-between">
          <button
            onClick={handlePreviousMonth}
            disabled={!canGoPrevious || isTransitioning || isLoadingCalendar}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ color: game.signatureColor }}
            aria-label="Previous month"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          <div className={`text-3xl font-bold uppercase tracking-[2px] text-gray-900 dark:text-gray-100 transition-opacity duration-150 ${isTransitioning || isLoadingCalendar ? 'opacity-50' : 'opacity-100'}`}
               style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            {format(currentMonth, 'MMMM yyyy')}
          </div>

          <button
            onClick={handleNextMonth}
            disabled={!canGoNext || isTransitioning || isLoadingCalendar}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ color: game.signatureColor }}
            aria-label="Next month"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>

        {/* Loading State */}
        {isLoadingCalendar && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2"
                 style={{ borderColor: game.signatureColor }}></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading challenges...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoadingCalendar && calendarData && calendarData.challenges.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">No Challenges This Month</h3>
            <p className="text-gray-600 dark:text-gray-400">
              There are no challenges available for {format(currentMonth, 'MMMM yyyy')}.
            </p>
          </div>
        )}

        {/* Calendar Grid */}
        {!isLoadingCalendar && calendarData && calendarData.challenges.length > 0 && (
          <ArchiveCalendar
            calendarDays={calendarDays}
            isTransitioning={isTransitioning || isNavigating}
            handleChallengeClick={handleChallengeClick}
            signatureColor={game.signatureColor}
            challengeType={game.challengeType}
            numberOfStars={game.numberOfStars}
          />
        )}

        {/* Stats Summary */}
        {statsData && (
          <ArchiveStats
            stats={statsData}
            isLoading={isLoadingStats}
            signatureColor={game.signatureColor}
            scoreDescriptor={game.scoreDescriptor}
          />
        )}
      </main>

      {/* Premium Upsell Modal */}
      <PremiumUpsellModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onUpgrade={handleUpgrade}
        reason={upsellReason}
        signatureColor={game.signatureColor}
      />

      {/* Challenge Popup */}
      {selectedChallenge && (
        <GameChallengePopup
          game={game}
          challenge={{
            id: selectedChallenge.id,
            gameId: game.id,
            publishDate: selectedChallenge.publishDate.toISOString(),
            puzzleNumber: selectedChallenge.puzzleNumber,
            puzzleName: selectedChallenge.puzzleName,
            displayState: selectedChallenge.displayState,
            createdAt: '', // Not needed for display
            updatedAt: ''  // Not needed for display
          }}
          playerStats={popupPlayerStats}
          progress={selectedProgress}
          isCurrentPeriod={isCurrentPeriod}
          isLoading={popupDataLoading}
          error={popupDataError?.message || null}
          onClose={handlePopupClose}
          onPlay={handlePopupPlay}
          onRetry={refetchPopupData}
        />
      )}
    </div>
  )
}
